import React from "react";
import "./WhyChooseUs.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faCircleCheck,
  faHeadset,
  faBolt,
  faLock,
  faLaptop,
  faMobileScreen,
  faGlobe,
  faRotateRight,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons";
import { useLocalContext } from "../../context/LocalContext";

const WhyChooseUs = () => {
  const { webinfo = {} } = useLocalContext();
  const brandName = webinfo?.name || "Our Store";

  const highlights = [
    {
      icon: faShieldAlt,
      title: "Verified license delivery",
      text: "You receive a digital key via email with clear steps to activate, so you’re never left guessing after checkout.",
    },
    {
      icon: faBolt,
      title: "Faster plan matching",
      text: "We simplify the messy parts: device limits, duration, renewals, and what features actually come with each edition.",
    },
    {
      icon: faHeadset,
      title: "Activation help, not scripts",
      text: "If you’re stuck during install or activation, we guide you through the practical steps (download → sign in → key → confirm).",
    },
  ];

  const whatYouGet = [
    { icon: faCircleCheck, text: "License key + quick activation guide by email" },
    { icon: faGlobe, text: "Platform notes (Windows / macOS / Android / iOS)" },
    { icon: faReceipt, text: "Order confirmation + renewal/duration clarity" },
    { icon: faLock, text: "Pointers to vendor account & security settings" },
  ];

  const deviceClarity = [
    { icon: faLaptop, title: "Desktop/Laptop", text: "Most full-feature tools live here (firewall, web protection, advanced options)." },
    { icon: faMobileScreen, title: "Phone/Tablet", text: "Mobile editions can differ—great for web safety, but features vary by vendor." },
    { icon: faRotateRight, title: "Switching devices", text: "Many plans allow transfer within the device limit (vendor rules apply)." },
  ];

  const faqs = [
    {
      q: "What happens right after payment?",
      a: "You’ll receive the license key and an activation guide by email. If a vendor account is required, we include the official link to create/sign in.",
    },
    {
      q: "Can you recommend a plan for my devices?",
      a: `Yes. Tell us how many devices you want to cover and what you do (work, study, banking, gaming). ${brandName} will shortlist options and explain the tradeoffs.`,
    },
    {
      q: "Who handles advanced technical issues inside the app?",
      a: "If it’s a deep in-app error, the vendor’s support team is best. We help with purchase, activation, download, and basic setup checks.",
    },
    {
      q: "Do plans auto-renew?",
      a: "Most keys are time-bound. If auto-renewal exists, it’s typically managed in the vendor account. We’ll point you to the official controls.",
    },
  ];

  return (
    <section className="wc2" aria-labelledby="wc2-title">
      <div className="wc2-wrap">
        {/* Top header */}
        <header className="wc2-head">
          <span className="wc2-kicker">Why customers pick {brandName}</span>
          <h2 id="wc2-title" className="wc2-h2">
            Clear plans, instant delivery, and{" "}
            <span className="wc2-accent">real help</span>
          </h2>
          <p className="wc2-sub">
            Security software shouldn’t feel confusing. We focus on clarity
            (devices + duration + features), digital delivery, and guidance so
            you can activate confidently.
          </p>
        </header>

        {/* Highlights */}
        <div className="wc2-grid" role="list">
          {highlights.map((c, i) => (
            <article className="wc2-card" key={i} role="listitem">
              <div className="wc2-ico">
                <FontAwesomeIcon icon={c.icon} />
              </div>
              <div className="wc2-cardBody">
                <h3>{c.title}</h3>
                <p>{c.text}</p>
              </div>
            </article>
          ))}
        </div>

        {/* Middle split */}
        <div className="wc2-split">
          <div className="wc2-panel">
            <h3 className="wc2-h3">What you get with every order</h3>
            <ul className="wc2-list" role="list">
              {whatYouGet.map((x, i) => (
                <li key={i}>
                  <span className="wc2-dot" aria-hidden="true">
                    <FontAwesomeIcon icon={x.icon} />
                  </span>
                  <span>{x.text}</span>
                </li>
              ))}
            </ul>

            <div className="wc2-miniNote" role="note">
              We keep expectations realistic: feature availability can differ by
              plan tier, OS, and country/region.
            </div>
          </div>

          <div className="wc2-panel">
            <h3 className="wc2-h3">Device clarity (no surprises)</h3>

            <div className="wc2-deviceRows" role="list">
              {deviceClarity.map((d, i) => (
                <div className="wc2-device" key={i} role="listitem">
                  <div className="wc2-deviceIco">
                    <FontAwesomeIcon icon={d.icon} />
                  </div>
                  <div>
                    <p className="wc2-deviceTitle">{d.title}</p>
                    <p className="wc2-deviceText">{d.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="wc2-helpStrip">
              <div>
                <p className="wc2-helpTitle">Need help choosing?</p>
                <p className="wc2-helpText">
                  Share your device count + usage. We’ll recommend options
                  without pushing extras you don’t need.
                </p>
              </div>

              {webinfo?.phonecall ? (
                <a className="wc2-btn" href={`tel:${webinfo.phonecall}`}>
                  <FontAwesomeIcon icon={faHeadset} />{" "}
                  {webinfo?.phone ? `Call ${webinfo.phone}` : "Talk to us"}
                </a>
              ) : (
                <span className="wc2-btn ghost" aria-disabled="true">
                  <FontAwesomeIcon icon={faHeadset} /> Support available
                </span>
              )}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="wc2-faq">
          <h3 className="wc2-h3">Common questions</h3>
          <div className="wc2-faqGrid">
            {faqs.map((f, i) => (
              <details className="wc2-faqItem" key={i}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
